import { useEffect } from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { trackServerPageView, trackButtonClick, setupPageTimeTracking } from "../lib/analytics";

const plans = [
  { name: "DSCSA Compliance", price: "$10", period: "/month", desc: "Drug supply chain tracking", features: ["Transaction tracking", "Serialization verification", "Audit-ready reports"], popular: false },
  { name: "Atlas Inventory", price: "$200", period: "/month", desc: "Smart inventory management", features: ["Real-time stock tracking", "Auto purchase orders", "Low stock alerts", "Cycle counting & FEFO"], popular: false },
  { name: "ScriptTrack Cash", price: "$500", period: "/month", desc: "Full dispensing for cash-pay", features: ["Everything in Atlas", "Patient management", "Prescription workflow", "DEA compliant controls", "Integrated POS system"], popular: true },
  { name: "ScriptTrack Full", price: "$750", period: "/month", desc: "E-Script + Insurance billing", features: ["Everything in Cash", "E-Prescribing (Surescripts)", "Real-time insurance billing", "NCPDP D.0 claims", "Eligibility checking"], popular: false },
];

export default function Software() {
  useEffect(() => {
    trackServerPageView("/software", "software_page_view");
    return setupPageTimeTracking("/software");
  }, []);

  return (
    <>
      <SEO
        title="ScriptTrack — Dispensing & DSCSA Software"
        description="ScriptTrack: in-office dispensing, e-prescribing, real-time insurance billing, and DSCSA compliance. Pricing from $10/month. DEA-compliant, PDMP-integrated."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Software", path: "/software" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "ScriptTrack",
          applicationCategory: "HealthApplication",
          operatingSystem: "Web",
          description:
            "In-office dispensing, DSCSA compliance, e-prescribing, and real-time insurance billing for physicians and clinics.",
          url: "https://premierpharma.com/software",
          offers: [
            {
              "@type": "Offer",
              name: "DSCSA Compliance",
              price: "10",
              priceCurrency: "USD",
              priceValidUntil: "2027-08-10",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                billingPeriod: "P1M",
              },
            },
            {
              "@type": "Offer",
              name: "Atlas Inventory",
              price: "200",
              priceCurrency: "USD",
              priceValidUntil: "2027-08-10",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                billingPeriod: "P1M",
              },
            },
            {
              "@type": "Offer",
              name: "ScriptTrack Cash",
              price: "500",
              priceCurrency: "USD",
              priceValidUntil: "2027-08-10",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                billingPeriod: "P1M",
              },
            },
            {
              "@type": "Offer",
              name: "ScriptTrack Full",
              price: "750",
              priceCurrency: "USD",
              priceValidUntil: "2027-08-10",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                billingPeriod: "P1M",
              },
            },
          ],
          publisher: { "@id": "https://premierpharma.com/#organization" },
        }}
      />
      <section className="py-20 bg-gradient-to-br from-background to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex gap-2 justify-center mb-6">
            <span className="bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-medium">DEA Compliant</span>
            <span className="bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-medium">E-Script</span>
            <span className="bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-medium">PDMP Integrated</span>
          </div>
          <h1 className="text-5xl font-bold text-primary">ScriptTrack — In-Office Dispensing &amp; DSCSA Compliance Software</h1>
          <p className="mt-4 text-lg text-accent font-semibold">Turn every prescription into practice revenue.</p>
          <p className="mt-2 text-lg text-gray-600 max-w-2xl mx-auto">ScriptTrack enables physicians to dispense medications in-office, adding $26-29 profit per prescription while maintaining full regulatory compliance.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primary text-center mb-8">Simple, Transparent Pricing</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, i) => (
              <div key={i} className={`card relative ${plan.popular ? "ring-2 ring-accent" : ""}`}>
                {plan.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full">POPULAR</span>}
                <h3 className="font-bold text-primary text-lg">{plan.name}</h3>
                <p className="text-gray-500 text-sm mt-1">{plan.desc}</p>
                <div className="mt-4 mb-4">
                  <span className="text-3xl font-bold text-primary">{plan.price}</span>
                  <span className="text-gray-500 text-sm">{plan.period}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" onClick={() => trackButtonClick("Get Started", `pricing-card-${plan.name.toLowerCase().replace(/\s+/g, "-")}`)} className={`block text-center py-2 rounded-btn font-semibold text-sm ${plan.popular ? "btn-primary" : "btn-outline"}`}>Get Started</Link>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">All plans include free setup, training, and 24/7 support. 100% money-back guarantee.</p>
        </div>
      </section>
    </>
  );
}
